<template>
  <div v-loading="scene_state.loading"
       :element-loading-text="$t('Scene is being prepared')"
       class="sku-validation module-container"
       :class="currentStateClass">
    <template v-if="scene_state.ready">
      <div class="nav module-container__header">
        <div class="module-header-top">
          <el-breadcrumb separator-class="el-icon-arrow-right">
            <el-breadcrumb-item :to="{ name: Breadcrumbs.visitsDetail, params: { visitId: visitId } }">
              {{ scene_data.visit.display_name }}
            </el-breadcrumb-item>
            <el-breadcrumb-item>
              {{ $t('SKU Inspection') }}
            </el-breadcrumb-item>
          </el-breadcrumb>
          <ProcessedSkuIndicator :show-processed-count="bus.edit_mode"
                                 :show-type="true"
                                 :counts="processedSkuCount.visit"
                                 :visit-level-crops="visitLevelCrops"
                                 :image-set-level-crops="imageSetLevelCrops"
                                 :image-level-crops="imageLevelCrops"
                                 :name="$t('Visit')"
                                 @addBulkCropToPool="handleAddBulkCropToPool" />
        </div>
      </div>
      <div class="aside">
        <div class="aside__title">
          {{ $t('Imagesets') }}
        </div>

        <div class="image-set-list">
          <el-collapse accordion :value="current_image_set_index">
            <el-collapse-item v-for="(image_set,index) in scene_data.image_sets"
                              :key="index"
                              :name="index"
                              :class="getImageSetListItemClass(image_set)">
              <template slot="title">
                <el-row @click="selectImageSet(index)">
                  <el-col :span="13">
                    <span :title="image_set.display_type + ' - ' + image_set.category_name">{{ (image_set.display_type + " - " + image_set.category_name) | truncate(15) }}</span>
                  </el-col>
                  <el-col class="count-cell" :span="2">
                    <el-button v-if="image_set.task_id && (isDeveloper || savantLinkEnabled)"
                               class="savant-btn"
                               type="text"
                               @click="openInSavant(image_set.task_id)">
                      <img src="/statics/savant-icon.png" style="width: 21px; height: 21px;">
                    </el-button>
                  </el-col>
                  <el-col class="count-cell" :span="3">
                    <span class="sku">{{ image_set.normal_sku_count }}</span>
                  </el-col>
                  <el-col class="count-cell" :span="3">
                    <span class="unrecognized">{{ image_set.unrecognized_sku_count }}</span>
                  </el-col>
                  <el-col class="count-cell" :span="3">
                    <span class="meta">{{ image_set.meta_sku_count }}</span>
                  </el-col>
                </el-row>
              </template>

              <div v-for="(image,imageIndex) in image_set.images"
                   :key="imageIndex"
                   class="image-set-list-image"
                   :class="{ active: current_image_index === imageIndex && current_image_set_index === index }"
                   @click.stop="selectImage(index,imageIndex)">
                <el-row>
                  <el-col :span="15">
                    <span class="index">#{{ imageIndex + 1 }}</span>
                  </el-col>
                  <el-col class="count-cell" :span="3">
                    <span class="sku-count">{{ image.normal_sku_count }}</span>
                  </el-col>
                  <el-col class="count-cell" :span="3">
                    <span class="unrecognized-sku-count">{{ image.unrecognized_sku_count }}</span>
                  </el-col>
                  <el-col class="count-cell" :span="3">
                    <span class="meta-sku-count">{{ image.meta_sku_count }}</span>
                  </el-col>
                </el-row>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>
      <div class="main-nav">
        <div class="top-nav">
          {{ current_image_set.display_type }} - {{ current_image_set.category_name }}
          <ProcessedSkuIndicator :show-processed-count="bus.edit_mode"
                                 :counts="processedSkuCount.image_set"
                                 :visit-level-crops="visitLevelCrops"
                                 :image-set-level-crops="imageSetLevelCrops"
                                 :image-level-crops="imageLevelCrops"
                                 :name="$t('Imageset')"
                                 @addBulkCropToPool="handleAddBulkCropToPool" />
        </div>
        <div class="sub-nav">
          <div class="sub-nav__button-wrapper">
            <div class="indicator">
              {{ $t('Image') }} {{ current_image_index + 1 }} / {{ current_image_set.images.length }}
            </div>
            <el-button-group>
              <el-button :disabled="disabledPrev" @click="handlePrev">
                <i class="el-icon-arrow-left" />
                {{ $t('Prev Image') }}
              </el-button>
              <el-button :disabled="disabledNext" @click="handleNext">
                {{ $t('Next Image') }}
                <i class="el-icon-arrow-right" />
              </el-button>
            </el-button-group>
          </div>
          <ProcessedSkuIndicator :show-processed-count="bus.edit_mode"
                                 :counts="processedSkuCount.image"
                                 :visit-level-crops="visitLevelCrops"
                                 :image-set-level-crops="imageSetLevelCrops"
                                 :image-level-crops="imageLevelCrops"
                                 :name="$t('Image')"
                                 @addBulkCropToPool="handleAddBulkCropToPool" />
        </div>
      </div>
      <div class="main-switchers">
        <div class="sku-actions">
          <el-switch v-model="bus.show_normal_sku"
                     active-color="#409eff"
                     active-text="SKU"
                     @change="bus.toggleSkuByType">
            {{ $t('SKU') }}
          </el-switch>
          <el-switch v-model="bus.show_unrecognized_sku"
                     active-color="#57e0c1"
                     active-text="UNRECOGNIZED"
                     @change="bus.toggleSkuByType">
            {{ $t('Unrecognized') }}
          </el-switch>
          <el-switch v-model="bus.show_meta_sku"
                     active-color="#ecd930"
                     active-text="META"
                     @change="bus.toggleSkuByType">
            {{ $t('Meta') }}
          </el-switch>
          <el-switch v-if="processedSkuCount.visit.nonSku.total"
                     v-model="bus.show_non_sku"
                     active-color="#ff6600"
                     active-text="NON SKU"
                     @change="bus.toggleSkuByType">
            {{ $t('Non Sku') }}
          </el-switch>
          <el-checkbox v-model="bus.hide_competitor_sku"
                       @change="bus.toggleSkuByType">
            {{ $t('Hide Competitor') }}
          </el-checkbox>
        </div>
        <div v-if="allow_sku_validation_editing || isDeveloper" class="editing">
          <div v-if="isApproved">
            {{ $t('Approved') }} <br>
            <small>{{ $t('Sent to approve by - {name}', { name: validationUser }) }}</small>
          </div>

          <div v-else-if="isSealed">
            <el-button v-if="isDeveloper"
                       type="success"
                       class="approve-button"
                       @click="approveValidation">
              {{ $t('Approve') }}
            </el-button>
            <small v-else>
              {{ isSealed && $t('Sent to approve by - {name}', { name: validationUser }) }}
            </small>
          </div>
          <div v-else>
            <el-switch v-if="!validationUser || currentUser === validationUser"
                       v-model="bus.edit_mode"
                       type="primary"
                       :active-text="$t('Edit Mode')"
                       @change="bus.emitEditMode" />
            <small v-else>
              {{ $t('Being processed by - {name}', { name: validationUser }) }}
            </small>
          </div>
        </div>
      </div>
      <div class="main">
        <Scene :project-id="projectId"
               :scene="current_image"
               :can-undo="canUndo"
               :non-sku-list="NonSkuList.data" />
        <div v-if="bus.edit_mode" class="main-search">
          <SkuSearchForm :project-id="projectId"
                         :sku-group-id="current_image_set.sku_group_id"
                         @apply="bus.applyObjection" />
          <el-button type="danger"
                     class="seal-button"
                     :disabled="isSealButtonDisabled"
                     @click="bus.sealValidation">
            {{ $t('Done') }}
          </el-button>
        </div>
      </div>
      <el-dialog
        v-loading="bulkCrops.bulkImageProcessing"
        class="add-bulk-crop-dialog"
        :visible.sync="addBulkCropDialog"
        :show-close="false"
        width="27%">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="dialog-info" v-html="addBulkCropWarningText" />
        <span slot="footer" class="dialog-footer">
          <el-button class="footer-button add"
                     type="primary"
                     @click="addBulkCropItems">{{ $t('Add') }}</el-button>
          <el-button class="footer-button cancel" @click="addBulkCropDialog = false">Cancel</el-button>
        </span>
      </el-dialog>
    </template>
    <span v-else-if="scene_state.error">
      {{ scene_state.error }}
    </span>
    <SkuCandidatePool :project-id="projectId" />
  </div>
</template>

<script>
import {
  chain, result, map, assign
} from 'lodash';
import { createCrudState } from 'vuex-module-generator';
import { mapState, mapGetters } from 'vuex';
import { Message } from 'element-ui';
import { fetchSkus, approveVisitValidation } from '@/shared/apis';
import Scene from './components/Scene';
import SkuSearchForm from './components/SkuSearchForm';
import bus from './sku-validation-scene/bus';
import { fetchVisitSkuValidations } from './api';
import { fetchNonSkuClassesWithParams } from '../all-skus/api';
import ProcessedSkuIndicator from './components/ProcessedSkuIndicator';
import errorResolver from '../../helpers/errorResolver';
import { getSceneData } from './utils';
import componentBus, { EVENTS as componentBusEvents, EVENTS } from './components/componentBus';
import SkuCandidatePool from './components/SkuCandidatePool';

export default {
  name: 'SkuValidation',
  components: {
    SkuCandidatePool,
    Scene,
    SkuSearchForm,
    ProcessedSkuIndicator
  },
  props: {
    projectId: {
      type: [String, Number],
      required: true
    },
    visitId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      bus,
      componentBus,
      scene_data: {
        image_sets: [],
        normal_sku_count: 0,
        non_sku_count: 0,
        unrecognized_sku_count: 0,
        meta_sku_count: 0,
        visit: {}
      },
      scene_state: {
        ready: false,
        loading: false,
        error: false
      },
      current_image_set_index: 0,
      current_image_index: 0,
      MetaSkuList: createCrudState().list,
      CompetitorSkuList: createCrudState().list,
      NonSkuList: createCrudState().list,
      addBulkCropDialog: false,
      addBulkCropWarningText: '',
      bulkCrops: {
        currentType: '',
        currentLevel: '',
        bulkCropList: [],
        bulkCropImageUrls: [],
        bulkImageProcessing: false
      }
    };
  },
  computed: {
    ...mapState(['Account', 'Project']),
    ...mapGetters(['allow_sku_validation_editing', 'allow_sku_validation_approval']),
    isSealButtonDisabled() {
      const { meta, normal, unrecognized } = this.processedSkuCount.visit;

      return (meta.processed + normal.processed + unrecognized.processed) === 0;
    },
    currentStateClass() {
      let className = '';

      if (this.scene_state.loading) className = 'is-loading';
      if (this.scene_state.ready) className = 'is-ready';
      if (this.scene_state.error) className = 'is-error';

      return className;
    },
    processedSkuCount() {
      const {
        scene_data: {
          normal_sku_count,
          non_sku_count,
          unrecognized_sku_count,
          meta_sku_count
        },
        bus: {
          ValidatedCrops: {
            data: {
              body: validated_sku_list = []
            }
          }
        },
        current_image_set,
        current_image
      } = this;

      return {
        visit: {
          unrecognized: {
            total: unrecognized_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_unrecognized)
              .size().value()
          },
          meta: {
            total: meta_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_meta)
              .size().value()
          },
          normal: {
            total: normal_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => !i.is_meta && !i.is_unrecognized && i.image_id === current_image.image_id)
              .size().value()
          },
          nonSku: {
            total: non_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_nonSku)
              .size().value()
          }
        },
        image_set: {
          unrecognized: {
            total: current_image_set.unrecognized_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_unrecognized && i.image_set_id === current_image_set.image_set_id)
              .size().value()
          },
          meta: {
            total: current_image_set.meta_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_meta && i.image_set_id === current_image_set.image_set_id)
              .size().value()
          },
          normal: {
            total: current_image_set.normal_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_sku && i.image_set_id === current_image_set.image_set_id)
              .size().value()
          },
          nonSku: {
            total: current_image_set.non_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_nonSku && i.image_set_id === current_image_set.image_set_id)
              .size().value()
          }
        },
        image: {
          unrecognized: {
            total: current_image.unrecognized_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_unrecognized && i.image_id === current_image.image_id)
              .size().value()
          },
          meta: {
            total: current_image.meta_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_meta && i.image_id === current_image.image_id)
              .size().value()
          },
          normal: {
            total: current_image.normal_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_sku && i.image_id === current_image.image_id)
              .size().value()
          },
          nonSku: {
            total: current_image.non_sku_count,
            processed: chain(validated_sku_list)
              .filter(i => i.is_nonSku && i.image_id === current_image.image_id)
              .size().value()
          }
        }
      };
    },
    disabledNext() {
      const {
        scene_data: {
          image_sets
        },
        current_image_set,
        current_image_set_index,
        current_image_index
      } = this;

      const total_image_set = image_sets.length - 1;
      const total_images = current_image_set.images.length - 1;

      return total_image_set === current_image_set_index && total_images === current_image_index;
    },
    disabledPrev() {
      const { current_image_set_index, current_image_index } = this;

      return current_image_set_index === 0 && current_image_index === 0;
    },
    current_image_set() {
      return this.scene_data.image_sets[this.current_image_set_index];
    },
    current_image() {
      return this.current_image_set.images[this.current_image_index];
    },
    validationUser() {
      return result(bus.VisitSkuValidation.data, 'sku_validation.user', null);
    },
    isSealed() {
      return result(bus.VisitSkuValidation.data, 'sku_validation.sealed');
    },
    isDeveloper() {
      return this.Account.active.data.developer;
    },
    isApproved() {
      return result(bus.VisitSkuValidation.data, 'sku_validation.approved');
    },
    currentUser() {
      return this.Account.active.data.email;
    },
    canUndo() {
      if (this.isApproved) {
        return false;
      }

      if (this.isDeveloper) {
        return true;
      }

      if (this.isSealed) {
        return false;
      }

      if (this.currentUser === this.validationUser) {
        return true;
      }

      return false;
    },
    savantLinkEnabled() {
      return (this.Project.active.data.settings.savant_link_enabled !== undefined) && (this.Project.active.data.settings.savant_link_enabled === true) && (this.Account.active.data.savant);
    },
    visitLevelCrops() {
      return chain(this.scene_data)
        .result('image_sets', [])
        .flatMap(obj => map(obj.images, 'crops'))
        .flatten()
        .value();
    },
    imageSetLevelCrops() {
      return chain(this.current_image_set)
        .result('images', [])
        .map('crops')
        .flatten()
        .value();
    },
    imageLevelCrops() {
      return chain(this.current_image).result('crops', []).value();
    }
  },
  async mounted() {
    bus.visitId = this.visitId;
    bus.projectId = this.projectId;

    if (!this.Account.active.data.developer) {
      if (this.allow_sku_validation_approval !== undefined) {
        bus.allow_approval = this.allow_sku_validation_approval;
      }
    } else {
      bus.allow_approval = false;
    }

    this.scene_state.loading = true;

    try {
      await this.fetchMetaSkus();
      await this.fetchCompetitorSkus();
      await this.fetchNonSkus();
      await this.fetchVisitSkuValidations();
      this.populateSceneData();
      if (this.scene_data.image_sets.length) {
        document.addEventListener('keydown', this.listenKeyboardEvents);
        this.scene_state.ready = true;
      } else {
        this.scene_state.error = 'There is no reported image set to validate for this visit';
      }
    } catch (error) {
      this.scene_state.error = errorResolver(error).message;
    }
    this.scene_state.loading = false;
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.listenKeyboardEvents);
    componentBus.$off();
  },
  methods: {
    getImageSetListItemClass(image_set) {
      const { image_set_id } = image_set;
      const {
        current_image_set,
        bus: {
          ValidatedCrops: {
            data: {
              body: validated_sku_list = []
            }
          }
        }
      } = this;

      const dirty = validated_sku_list.find(item => item.image_set_id === image_set_id) ? 'dirty' : '';

      const active = current_image_set.image_set_id === image_set_id ? 'active' : '';

      return `${dirty} ${active}`;
    },
    listenKeyboardEvents(e) {
      const {
        scene_data: {
          image_sets
        }
      } = this;

      const total_image_set = image_sets.length - 1;

      if (e.code === 'ArrowDown') {
        if (total_image_set !== this.current_image_set_index) {
          this.current_image_set_index += 1;
          this.current_image_index = 0;
        }
        this.scrollIntoViewImageSetList();
      }

      if (e.code === 'ArrowUp') {
        if (!this.disabledPrev) {
          this.current_image_set_index -= 1;
          this.current_image_index = 0;
        }
        this.scrollIntoViewImageSetList();
      }
    },
    selectImageSet(index) {
      componentBus.$emit(EVENTS.IMAGE_SET_CHANGED);

      this.current_image_set_index = index;
      this.current_image_index = 0;
    },
    selectImage(imageSetIndex, imageIndex) {
      this.current_image_set_index = imageSetIndex;
      this.current_image_index = imageIndex;
    },
    handleNext() {
      const { current_image_set, current_image_index } = this;

      const total_images = current_image_set.images.length - 1;

      if (total_images !== current_image_index) {
        this.current_image_index += 1;
      } else {
        this.current_image_set_index += 1;
        this.current_image_index = 0;
      }

      this.scrollIntoViewImageSetList();
    },
    handlePrev() {
      const { current_image_index, scene_data: { image_sets } } = this;

      if (current_image_index === 0) {
        this.current_image_set_index -= 1;
        const prev_image_set = image_sets[this.current_image_set_index];
        this.current_image_index = prev_image_set.images.length - 1;
      } else {
        this.current_image_index -= 1;
      }

      this.scrollIntoViewImageSetList();
    },
    scrollIntoViewImageSetList() {
      setTimeout(() => {
        this.$el.querySelector('.image-set-list__item.active')
          .scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 0);
    },
    async fetchMetaSkus() {
      this.MetaSkuList.isLoading = true;

      return fetchSkus(this.projectId, { per: 5000, search: { meta: true } })
        .then(({ data: { data } }) => {
          this.MetaSkuList.data = data;

          return Promise.resolve(data);
        })
        .catch((err) => {
          this.MetaSkuList.hasError = true;

          return Promise.reject(err);
        })
        .finally(() => {
          this.MetaSkuList.isLoading = false;
        });
    },
    async fetchCompetitorSkus() {
      this.CompetitorSkuList.isLoading = true;

      return fetchSkus(this.projectId, { per: 5000, sku_group_tag_name: 'competitor' })
        .then(({ data: { data } }) => {
          this.CompetitorSkuList.data = data;

          return Promise.resolve(data);
        })
        .catch((err) => {
          this.CompetitorSkuList.hasError = true;

          return Promise.reject(err);
        })
        .finally(() => {
          this.CompetitorSkuList.isLoading = false;
        });
    },
    async fetchNonSkus() {
      this.NonSkuList.isLoading = true;

      return fetchNonSkuClassesWithParams(this.projectId, { per: 5000 })
        .then(({ data: { data } }) => {
          this.NonSkuList.data = data;

          return Promise.resolve(data);
        })
        .catch((err) => {
          this.NonSkuList.hasError = true;

          return Promise.reject(err);
        })
        .finally(() => {
          this.NonSkuList.isLoading = false;
        });
    },
    async fetchVisitSkuValidations() {
      bus.VisitSkuValidation.isLoading = true;

      return fetchVisitSkuValidations(this.projectId, this.visitId)
        .then(({ data: { data } }) => {
          bus.VisitSkuValidation.data = data;

          if (data.sku_validation) {
            bus.ValidatedCrops.data = data.sku_validation;
          }

          return Promise.resolve(data);
        })
        .catch((err) => {
          bus.VisitSkuValidation.hasError = true;

          return Promise.reject(err);
        })
        .finally(() => {
          bus.VisitSkuValidation.isLoading = false;
        });
    },
    approveValidation() {
      return this.$confirm(
        this.$t('This visit will be approved. This action can not be undone'),
        this.$t('Confirmation'),
        {
          confirmButtonText: this.$t('Approve'),
          cancelButtonText: this.$t('Cancel')
        }
      )
        .then(() => approveVisitValidation(this.projectId, this.visitId, bus.VisitSkuValidation.data.sku_validation.id))
        .then(() => {
          Message(this.$t('Visit was approved'));
          window.location.reload();
        })
        .catch((err) => {
          this.ValidatedCrops.hasError = true;
          Message(this.$t('Visit couldn\'t approved.'));

          return Promise.reject(err);
        });
    },
    populateSceneData() {
      this.scene_data = getSceneData(bus.VisitSkuValidation, this.MetaSkuList.data, this.CompetitorSkuList.data);
    },
    openInSavant(taskId) {
      window.location.href = `savant:?taskId=${taskId}`;
    },
    handleAddBulkCropToPool(payload) {
      switch (payload.type) {
        case 'sku':
          this.addBulkCropWarningText = this.$t('Are you sure you want to add <span>{count}</span> SKUS to the Pool?', { count: payload.cropList.length });
          break;
        case 'unrecognized':
          this.addBulkCropWarningText = this.$t('Are you sure you want to add <span>{count}</span> Unrecognized SKUS to the Pool?', { count: payload.cropList.length });
          break;
        case 'meta':
          this.addBulkCropWarningText = this.$t('Are you sure you want to add <span>{count}</span> Meta SKUS to the Pool?', { count: payload.cropList.length });
          break;
        default:
          break;
      }

      assign(this.bulkCrops, {
        bulkCropList: payload.cropList,
        bulkCropImageUrls: [],
        currentLevel: payload.level,
        currentType: payload.type
      });

      this.addBulkCropDialog = true;
    },
    async addBulkCropItems() {
      this.bulkCrops.bulkImageProcessing = true;
      await this.processCropList(this.bulkCrops.bulkCropList);
      this.bulkCrops.bulkImageProcessing = false;

      const images = this.bulkCrops.bulkCropImageUrls;

      await componentBus.$emit(componentBusEvents.POST_BULK_SKU_CANDIDATE, images);

      this.addBulkCropDialog = false;
      Message(this.$t('Successfuly added to pool'));
    },
    async processCropList(cropList) {
      const bulkCropImageUrls = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const crop of cropList) {
        const imageURL = this.scene_data.image_sets
          .find(imageset => imageset.image_set_id === crop.image_set_id)
          .images
          .find(image => image.image_id === crop.image_id)
          .url;

        const { bottom_right, top_left } = crop.boundaries;
        const cropWidth = (bottom_right.x - top_left.x);
        const cropHeight = (bottom_right.y - top_left.y);

        const image = new Image();
        image.crossOrigin = 'anonymous';

        // eslint-disable-next-line no-await-in-loop,no-loop-func
        await new Promise((resolve) => {
          image.onload = () => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = image.width;
            canvas.height = image.height;
            context.drawImage(image, 0, 0);

            const top_left_x = top_left.x;
            const top_left_y = top_left.y;
            const width = cropWidth;
            const height = cropHeight;

            const newCanvas = document.createElement('canvas');
            const newContext = newCanvas.getContext('2d');
            newCanvas.width = width;
            newCanvas.height = height;
            newContext.drawImage(canvas, top_left_x, top_left_y, width, height, 0, 0, width, height);

            const newImageUrl = newCanvas.toDataURL();
            bulkCropImageUrls.push(newImageUrl);
            resolve();
          };
          image.src = imageURL;
        });
      }
      this.bulkCrops.bulkCropImageUrls = bulkCropImageUrls;
    }
  }
};
</script>

<style lang="scss">
.sku-validation {
  height: 100vh;

  &.is-error,
  &.is-loading {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
  }

  &.is-ready {
    display: grid;
    grid-template-areas:
      "nav nav nav nav nav nav nav nav nav nav nav nav"
      "aside aside main-nav main-nav main-nav main-nav main-nav main-nav main-nav main-nav main-nav main-nav"
      "aside aside main-switchers main-switchers main-switchers main-switchers main-switchers main-switchers main-switchers main-switchers main-switchers main-switchers"
      "aside aside main main main main main main main main main main"
      "aside aside main main main main main main main main main main"
      "aside aside main main main main main main main main main main"
      "aside aside main main main main main main main main main main"
      "aside aside main main main main main main main main main main";
    grid-template-rows: 0.4fr 0.4fr 0.2fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
    align-self: stretch;
  }

  .nav {
    grid-area: nav;
    height: auto !important;
    padding: 0 20px;
  }

  .aside {
    grid-area: aside;
    padding: 10px;
    overflow-y: auto;
    border-right: 2px solid $polishWhite;

    &__title {
      padding: 5px 0 10px 0;
      font-size: 16px;
    }

    .el-row {
      width: 100%;
    }

    .count-cell {
      text-align: right;
    }

    .image-set-list {
      $meta: #ecd930;
      $unrecognized: #57e0c1;
      $sku: #4a90e2;

      .el-collapse {
        &-item {
          font-weight: bold;

          .savant-btn {
            display: none;
            padding: 0;
            vertical-align: middle;
          }

          :hover {
            .savant-btn {
              display: inline-block;
            }
          }

          &.active {
            background-color: $white;
          }

          &__header {
            padding: 0 4px 0 10px;
            font-size: 12px;
            font-weight: bold;
            cursor: pointer;
            background-color: transparent;
            border-radius: 20px;

            .el-row {
              padding-right: 6px;
            }

            .unrecognized {
              font-weight: bold;
              color: $unrecognized;
            }

            .meta {
              font-weight: bold;
              color: $meta;
            }

            .sku {
              font-weight: bold;
              color: $sku;
            }
          }

          &__wrap {
            padding: 10px;
            background-color: transparent;
          }

          &__content {
            padding: 0;
          }
        }

        .image-set-list-image {
          padding: 0 4px 0 10px;
          font-weight: bold;
          cursor: pointer;
          border-bottom: 1px solid rgb(230, 230, 230);

          &.active {
            background-color: #f1f1f1;
          }

          span {
            display: inline-block;
            padding: 1px 8px;
            margin-right: 5px;
          }

          .el-row {
            padding-right: 10px;
          }

          .index {
            color: black;
          }

          .unrecognized-sku-count {
            padding: 0;
            color: $unrecognized;
          }

          .meta-sku-count {
            padding: 0;
            color: $meta;
          }

          .sku-count {
            padding: 0;
            color: $sku;
          }
        }
      }
    }
  }

  .main-nav {
    display: flex;
    flex-direction: column;
    grid-area: main-nav;
    border-bottom: 2px solid $polishWhite;

    .top-nav {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 50px;
      padding: 0 20px;
    }

    .sub-nav {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;
      min-height: 50px;
      padding: 0 20px;
      background-color: $white;

      &__button-wrapper {
        display: flex;
        align-items: center;
        height: 100%;

        .indicator {
          display: flex;
          align-items: center;
          height: 100%;
          padding-right: 50px;
          font-size: 24px;
          color: #464646;
          border-right: 1px solid #e4e4e4;
        }

        .el-button-group {
          height: 100%;

          .el-button {
            height: 100%;
            color: #4a90e2;

            span {
              display: flex;
              align-items: center;
              font-size: 15px;
              font-style: normal;
              font-weight: 600;
              line-height: normal;
              text-transform: uppercase;
              letter-spacing: 0.375px;

              i {
                font-size: 24px;
                font-weight: normal;
              }
            }
          }
        }
      }
    }
  }

  .main-switchers {
    display: flex;
    grid-area: main-switchers;
    align-items: center;
    justify-content: center;
    padding: 15px;
    background-color: $white;

    .el-switch {
      margin-right: 10px;

      &__label {
        color: $greyArea;

        &.is-active {
          color: #222;
        }
      }
    }

    .sku-actions {
      display: flex;
      flex: 1;
      justify-content: center;

      .el-checkbox {
        margin-left: 40px;
      }
    }

    .editing {
      color: $darkGrey;

      .approve-button {
        margin-left: 15px;
      }
    }
  }

  .main {
    box-sizing: border-box;
    display: flex;
    grid-area: main;
    padding: 0 10px 20px 20px;
    background-color: $white;
  }

  .main-search {
    display: flex;
    flex: 0 0 300px;
    flex-direction: column;
    margin-left: 20px;
    background-color: $white;

    .sku-search-form {
      flex: 1;
    }

    .seal-button {
      width: auto;
      padding-top: 13px;
      padding-bottom: 13px;
      text-transform: uppercase;
    }
  }
}

.add-bulk-crop-dialog {
  .el-dialog {
    border-radius: 8px;

    .el-dialog__header {
      padding: 0;
    }

    .el-dialog__body {
      padding: 30px 30px 0 30px;

      .dialog-info {
        font-size: 18px;
        font-weight: 600;
        line-height: 26px;
        color: #000;
        text-align: center;
        word-break: break-word;
      }
    }

    .el-dialog__footer {
      .dialog-footer {
        display: flex;
        flex-direction: column;

        .footer-button {
          padding: 15px 18px;
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          line-height: 14px;
          text-transform: uppercase;

          &:first-child {
            margin-bottom: 5px;
          }
        }
      }
    }
  }
}
</style>
